// Demand value with optional offer / counter / settled lanes. Reads
// matter.workspace.demand (loose) or falls back to matter.workspace.demandAmount.

import { pick, type WidgetProps } from "./types";
import { WidgetCard } from "./WidgetCard";

interface DemandShape {
  amount?: number;
  sentDate?: string;
  offer?: number;
  counterOffer?: number;
  settled?: number;
}

function fmtMoney(n: number | undefined): string {
  if (n == null) return "—";
  return `$${n.toLocaleString()}`;
}

export function DemandValueWidget({ config, matter }: WidgetProps) {
  const d = pick<DemandShape>(matter.workspace, "demand");
  const fallbackAmount = pick<number>(matter.workspace, "demandAmount");
  const title = config.title ?? "Demand";

  if (!d && fallbackAmount == null) {
    return (
      <WidgetCard title={title}>
        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>No demand data on this matter.</div>
      </WidgetCard>
    );
  }

  const demandAmount = d?.amount ?? fallbackAmount;

  return (
    <WidgetCard title={title} sub={d?.sentDate ?? ""}>
      <div style={{ marginBottom: 10 }}>
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--muted)",
          }}
        >
          Demand
        </div>
        <div
          style={{
            fontVariantNumeric: "tabular-nums",
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "-0.03em",
            color: "var(--ink)",
          }}
        >
          {fmtMoney(demandAmount)}
        </div>
      </div>
      {(d?.offer != null || d?.counterOffer != null || d?.settled != null) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <SmallCell label="Offer" value={fmtMoney(d?.offer)} />
          <SmallCell label="Counter" value={fmtMoney(d?.counterOffer)} />
          <SmallCell label="Settled" value={fmtMoney(d?.settled)} />
        </div>
      )}
    </WidgetCard>
  );
}

function SmallCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--muted)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontVariantNumeric: "tabular-nums",
          fontSize: 13,
          fontWeight: 600,
          color: value === "—" ? "var(--muted)" : "var(--ink)",
        }}
      >
        {value}
      </div>
    </div>
  );
}
